/**
 * Confirm dialog shown before a paint operation (brush, eraser,
 * retouch, fill, AI) can commit pixels onto a non-image layer
 * (vector / shape / text). The paint surface is created by
 * `ensurePaintSurface`, which can't write meaningful pixels into a
 * vector / shape / text layer's renderer without first replacing it
 * with a `fabric.FabricImage`. The user-facing consequence is the
 * same as the warp prompt: editing as a {vector|shape|text} is lost.
 *
 * Mirrors `RasterizeBeforeWarpDialog` in tone, layout, and button
 * placement. Wording is action-aware so the user sees "before
 * painting" / "before erasing" / "before filling" etc.
 */

import type { LayerType } from '../types';
import { MODAL_OVERLAY, MODAL_SHEET_CHROME } from '../lib/modal-classes';

/** Paint action that triggered the prompt. Drives the title's
 *  verb so the user sees "before painting" / "before erasing" /
 *  "before filling" etc. */
export type RasterizeBeforePaintAction = 'brush' | 'eraser' | 'retouch' | 'fill' | 'ai';

interface RasterizeBeforePaintDialogProps {
  /** Display name of the layer the user is trying to paint on. */
  layerName: string;
  /** Source `LayerType` of the target. Drives the noun in the body
   *  copy — "vector path" / "shape" / "text" — so the consequence
   *  is concrete. */
  sourceType: LayerType;
  /** Which paint action triggered the prompt. */
  action: RasterizeBeforePaintAction;
  onCancel: () => void;
  /** Confirm: rasterize the layer to a paint surface AND retry the
   *  paint operation (when the caller passed a replay closure). */
  onConfirm: () => void;
}

const ACTION_VERB: Record<RasterizeBeforePaintAction, string> = {
  brush: 'painting',
  eraser: 'erasing',
  retouch: 'retouching',
  fill: 'filling',
  ai: 'this AI tool',
};

const SOURCE_NOUN: Partial<Record<LayerType, string>> = {
  vector: 'vector path',
  shape: 'shape',
  text: 'text',
};

export function RasterizeBeforePaintDialog({
  layerName,
  sourceType,
  action,
  onCancel,
  onConfirm,
}: RasterizeBeforePaintDialogProps) {
  const BTN_BASE =
    'px-4 py-2 text-sm rounded-md border border-border text-foreground hover:bg-accent';
  const noun = SOURCE_NOUN[sourceType] ?? 'layer';
  const verb = ACTION_VERB[action];
  return (
    <div className={MODAL_OVERLAY}>
      <div
        className={`bg-background shadow-xl ${MODAL_SHEET_CHROME} w-full max-w-md p-6 space-y-4`}
      >
        <h2 className="text-lg font-semibold">
          Rasterize &ldquo;{layerName}&rdquo; before {verb}?
        </h2>
        <p className="text-sm text-muted-foreground">
          {action === 'ai' ? 'This AI tool' : `${verb.charAt(0).toUpperCase()}${verb.slice(1)}`}{' '}
          works on pixel layers. Continuing converts this {noun} to pixels — you will no longer be
          able to edit it as a {noun}.
        </p>
        <div className="flex justify-end gap-2 pt-2 flex-wrap">
          <button type="button" className={BTN_BASE} onClick={() => onCancel()}>
            Cancel
          </button>
          <button
            type="button"
            className={`${BTN_BASE} bg-primary text-primary-foreground border-primary hover:bg-primary/90`}
            onClick={() => onConfirm()}
          >
            Rasterize &amp; Continue
          </button>
        </div>
      </div>
    </div>
  );
}
